<template>
  <!-- TOOLBAR -->
  <div class="meeting-toolbar">
    <MeetingName :value="name" />
    <!-- Settings -->
    <v-col
      cols="6"
      md="4"
      class="d-flex justify-start justify-md-center"
    >
      <v-btn
        class="mx-1"
        dark
        x-small
        :color="isAudioEnabled ? 'grey' : 'error'"
        fab
        @click="toggleAudioEnable()"
      >
        <v-icon>mdi-microphone{{ isAudioEnabled ? "" : "-off" }}</v-icon>
      </v-btn>
      <v-btn
        class="mx-1"
        dark
        x-small
        :color="isVideoEnabled ? 'grey' : 'error'"
        fab
        @click="toggleVideoEnable()"
      >
        <v-icon>mdi-video{{ isVideoEnabled ? "" : "-off" }}</v-icon>
      </v-btn>
      <v-dialog
        transition="dialog-bottom-transition"
        max-width="600"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="mx-1"
            dark
            x-small
            color="grey"
            fab
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-cog</v-icon>
          </v-btn>
        </template>
        <template v-slot:default="dialog">
          <v-card>
            <v-toolbar
              color="primary"
              dark
              flat
            >
              <v-toolbar-title>Settings</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn
                icon
                @click="refreshInputs"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text class="mt-5">
              <v-select
                v-model="selectedAudioDevice"
                :items="audioDevices"
                item-text="label"
                label="Select audio input"
                prepend-icon="mdi-volume-high"
                return-object
                @change="setAudioDevice"
              />
              <v-select
                v-model="selectedVideoDevice"
                :items="videoDevices"
                item-text="label"
                label="Select video input"
                prepend-icon="mdi-video"
                return-object
                @change="setVideoDevice"
              />
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="justify-end">
              <v-btn
                text
                @click="dialog.value = false"
                >Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-dialog>
      <!-- DISCONNECT ROOM -->
      <v-btn
        class="mx-1 white--text"
        @click="toggleShareScreen()"
        fab
        x-small
        :color="isShareScreenEnabled ? 'primary' : 'grey'"
      >
        <v-icon>mdi-monitor-share</v-icon>
      </v-btn>
      <v-btn
        class="mx-1 white--text"
        @click="disconnect"
        fab
        x-small
        color="error"
      >
        <v-icon>mdi-phone-outline</v-icon>
      </v-btn>
    </v-col>

    <!-- Participants / Chat / Activities  -->
    <!-- MOBILE VERSION -->
    <v-col
      cols="6"
      md="4"
      class="d-flex justify-end pa-0"
      v-if="$vuetify.breakpoint.xsOnly"
    >
      <v-menu
        bottom
        left
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            dark
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-btn
            active-class=""
            class="mx-1"
            fab
            text
            x-small
            :color="selectedMenu === 'participants' ? 'success' : ''"
            @click="selectMenu('participants')"
          >
            <v-icon>
              mdi-account-multiple{{ selectedMenu !== "participants" ? "-outline" : "" }}
            </v-icon>
          </v-btn>
          <v-btn
            class="mx-1"
            fab
            text
            x-small
            :color="selectedMenu === 'chat' ? 'success' : ''"
            @click="
              chatMessages = 0
              selectMenu('chat')
            "
          >
            <v-badge
              color="warning"
              dot
              :value="chatMessages"
            >
              <v-icon>mdi-message-text{{ selectedMenu !== "chat" ? "-outline" : "" }}</v-icon>
            </v-badge>
          </v-btn>
          <v-btn
            class="mx-1"
            fab
            text
            x-small
            :retain-focus-on-click="false"
            :color="selectedMenu === 'activities' ? 'success' : ''"
            @click="selectMenu('activities')"
            v-if="$route.params.id"
          >
            <v-icon>mdi-shape{{ selectedMenu !== "activities" ? "-outline" : "" }}</v-icon>
          </v-btn>
        </v-list>
      </v-menu>
    </v-col>
    <!-- WEBSITE VERSION -->
    <v-col
      cols="6"
      md="4"
      v-else
      class="d-flex justify-end"
    >
      <v-btn
        class="mx-1"
        fab
        text
        dark
        x-small
        :color="selectedMenu === 'participants' ? 'success' : 'default'"
        @click="selectMenu('participants')"
      >
        <v-icon>
          mdi-account-multiple{{ selectedMenu !== "participants" ? "-outline" : "" }}
        </v-icon>
      </v-btn>
      <v-btn
        class="mx-1"
        fab
        text
        dark
        x-small
        :color="selectedMenu === 'chat' ? 'success' : 'default'"
        @click="
          chatMessages = 0
          selectMenu('chat')
        "
      >
        <v-badge
          color="warning"
          dot
          :value="chatMessages"
        >
          <v-icon>mdi-message-text{{ selectedMenu !== "chat" ? "-outline" : "" }}</v-icon>
        </v-badge>
      </v-btn>
      <v-btn
        class="mx-1"
        fab
        text
        x-small
        dark
        :retain-focus-on-click="false"
        :color="selectedMenu === 'activities' ? 'success' : 'default'"
        @click="selectMenu('activities')"
        v-if="$route.params.id"
      >
        <v-icon>mdi-shape{{ selectedMenu !== "activities" ? "-outline" : "" }}</v-icon>
      </v-btn>
    </v-col>
  </div>
  <!-- END TOOLBAR -->
</template>

<script>
import { createLocalAudioTrack, createLocalVideoTrack, LocalVideoTrack } from "twilio-video"
import MeetingName from "./MeetingName.vue"

export default {
  name: "MeetingToolbar",
  components: { MeetingName },
  props: {
    name: {
      type: String,
      default: ""
    },
    selectedMenu: {
      type: String,
      default: null
    },
    visioRoom: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      chatMessages: 0,
      devices: [],
      isAudioEnabled: false,
      isVideoEnabled: false,
      isShareScreenEnabled: false,
      selectedAudioDevice: null,
      selectedVideoDevice: null
    }
  },
  mounted() {
    this.initializeSettings()
  },
  computed: {
    audioDevices() {
      return this.devices.filter(e => e.kind === "audioinput")
    },
    videoDevices() {
      return this.devices.filter(e => e.kind === "videoinput")
    }
  },
  methods: {
    /**
     * Disconnect from meeting room
     */
    disconnect() {
      this.visioRoom.localParticipant.tracks.forEach(this.unpublish)
      this.visioRoom.disconnect()
    },
    /**
     * Load list of available media device from naviguator
     */
    async loadInputDevices() {
      return navigator.mediaDevices.enumerateDevices().then(devices => {
        this.devices = devices.map(e => {
          return {
            deviceId: e.deviceId,
            label: e.label || "no-label",
            kind: e.kind
          }
        })
      })
    },
    /**
     * Initialize component data from current room values
     * and available media devices
     */
    async initializeSettings() {
      await this.loadInputDevices()

      const localAudio = this.getFirstAudioPublication()
      this.isAudioEnabled = localAudio?.isTrackEnabled || false

      const audioLabel = localAudio?.track.mediaStreamTrack.label
      this.selectedAudioDevice = this.audioDevices.find(e => e.label === audioLabel)

      const localVideo = this.getFirstVideoPublication()
      this.isVideoEnabled = localVideo?.isTrackEnabled || false

      const videoLabel = localVideo?.track.mediaStreamTrack.label
      this.selectedVideoDevice = this.videoDevices.find(e => e.label === videoLabel)
    },
    getFirstAudioPublication() {
      const localParticipant = this.visioRoom.localParticipant
      return localParticipant.audioTracks.values().next().value
    },
    getFirstVideoPublication() {
      const localParticipant = this.visioRoom.localParticipant
      return localParticipant.videoTracks.values().next().value
    },
    /**
     * Notify User by poping up message in snackbar
     * @param {*} msg     The message to display
     * @param {*} color   The snackbar color to use
     */
    notify(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    /**
     * Publish local track. Stop track if failed
     * @param {*} track The track to publish
     */
    publishLocalTrack(track) {
      this.visioRoom.localParticipant.publishTrack(track).catch(error => {
        console.error(error)
        this.notify("Track cannot be published", "error")
        track.stop()
      })
    },
    /**
     * Force reset audio/video input and reload list of available inputs
     */
    refreshInputs() {
      this.setAudioDevice(this.selectedAudioDevice)
      this.setVideoDevice(this.selectedVideoDevice)
      this.loadInputDevices()
    },
    /**
     * Notify parent component that a new menu has been selected
     * @param {*} value   The selected menu value
     */
    selectMenu(value) {
      const newValue = this.selectedMenu === value ? null : value
      this.$emit("select", newValue)
    },
    setMediaDevice(createTrackMethod, setTrackMethod, newMediaDevice) {
      const trackOptions = { deviceId: { exact: newMediaDevice.deviceId } }
      createTrackMethod(trackOptions)
        .then(setTrackMethod)
        .catch(error => {
          this.notify(error, "error")
        })
    },
    setAudioDevice(newMediaDevice) {
      this.setMediaDevice(createLocalAudioTrack, this.setAudioTrack, newMediaDevice)
    },
    setVideoDevice(newMediaDevice) {
      this.setMediaDevice(createLocalVideoTrack, this.setVideoTrack, newMediaDevice)
    },
    setAudioTrack(track) {
      this.visioRoom.localParticipant.audioTracks.forEach(this.unpublish)
      this.publishLocalTrack(track)
    },
    setVideoTrack(track) {
      this.visioRoom.localParticipant.videoTracks.forEach(this.unpublish)
      this.publishLocalTrack(track)
    },
    toggleAudioEnable() {
      this.isAudioEnabled = !this.isAudioEnabled
      this.visioRoom.localParticipant.audioTracks.forEach(publication => {
        publication.track.enable(this.isAudioEnabled)
      })
    },
    toggleVideoEnable() {
      this.isVideoEnabled = !this.isVideoEnabled
      this.visioRoom.localParticipant.videoTracks.forEach(publication => {
        publication.track.enable(this.isVideoEnabled)
      })
    },
    async toggleShareScreen() {
      this.isShareScreenEnabled = !this.isShareScreenEnabled
      if (this.isShareScreenEnabled) {
        // Select screen sharing from navigator and update video track
        navigator.mediaDevices
          .getDisplayMedia({ video: { frameRate: 15 } })
          .then(stream => {
            const mediastreamtrack = stream.getVideoTracks()[0]
            const newTrack = new LocalVideoTrack(mediastreamtrack)

            // Set new video track
            this.setVideoTrack(newTrack)

            // Handle case screensharing is stopped by naviguator
            mediastreamtrack.onended = this.toggleShareScreen
          })
          .catch(error => {
            console.error(error)
            this.notify("ScreenSharing cannot be enabled", "error")
            this.isShareScreenEnabled = false
          })
      } else {
        // Restore selected camera device
        this.setVideoDevice(this.selectedVideoDevice)
      }
    },
    /**
     * Unpublish (stop/unpublish/detach) local publication
     * @param {*} localPublication The local publication
     */
    unpublish(localPublication) {
      localPublication.track.stop()
      localPublication.unpublish()
      localPublication.track.detach()
    }
  }
}
</script>

<style lang="scss" scoped>
$bgcolor-toolbar: rgb(30, 28, 28);
$border: 1px solid black;

.meeting-toolbar {
  border: $border;
  background-color: $bgcolor-toolbar;
  display: flex;
  flex: 0 1 70px;
}
</style>
